import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, Phone, Mail, MapPin } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { categories } from '../mockData';
import CategoryCard from '../components/CategoryCard';
import SEO from '../components/SEO';

const Home = () => {
  const trustBadges = [
    {
      title: 'Expert Guidance',
      description: 'Professional staff with years of experience'
    },
    {
      title: 'Top-Quality Products',
      description: 'Only the best from trusted manufacturers'
    },
    {
      title: 'Fast, Reliable Shipping',
      description: 'Quick delivery and professional setup'
    }
  ];

  return (
    <div className="min-h-screen">
      <SEO 
        title="Kalorama Medical - Durable Medical Equipment | Wheelchairs, Walkers & More"
        description="Your trusted partner for quality durable medical equipment. Wheelchairs, walkers, hospital beds, bath safety equipment and more. Expert guidance, insurance accepted."
        keywords="medical equipment, durable medical equipment, wheelchairs, walkers, hospital beds, mobility aids"
      />
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#1e3a5f] to-[#2c5282] text-white py-20 md:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=1600&q=80')] bg-cover bg-center opacity-10"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
              Independence. Delivered.
            </h1>
            <p className="text-xl md:text-2xl mb-4 text-blue-100">
              Your Trusted Partner for
            </p>
            <p className="text-xl md:text-2xl mb-8 text-blue-100">
              Durable Medical Equipment
            </p>
            <Link to="/products">
              <Button size="lg" className="bg-white text-[#1e3a5f] hover:bg-blue-50 text-lg px-8 py-6 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105">
                Explore Our Catalog
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Featured Categories */}
      <section className="py-16 bg-gradient-to-b from-blue-50 to-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-4 text-[#1e3a5f]">
            Featured Categories
          </h2>
          <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">
            Browse our extensive selection of medical equipment and supplies
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {categories.map((category) => (
              <CategoryCard key={category.id} category={category} />
            ))}
          </div>
        </div>
      </section>

      {/* Trust Badges */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {trustBadges.map((badge, index) => (
              <div key={index} className="flex items-start space-x-4 p-6 rounded-lg hover:bg-blue-50 transition-all duration-300">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-[#7bb5d4] rounded-full flex items-center justify-center">
                    <Check className="h-6 w-6 text-white" />
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2 text-[#1e3a5f]">
                    {badge.title}
                  </h3>
                  <p className="text-gray-600">{badge.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-16 bg-gradient-to-b from-white to-blue-50">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold mb-6 text-[#1e3a5f]">
                Innovating Health. Connecting Care.
              </h2>
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                At Kalorama Medical, we understand that selecting appropriate medical equipment means the difference between struggling with daily activities and living an easier, better life.
              </p>
              <p className="text-lg text-gray-700 mb-8 leading-relaxed">
                With years of experience in the healthcare industry, our dedicated team is here to offer you and your loved ones superior service with care and pride. We can help you make the best choices in today's rapidly changing healthcare world.
              </p>
              <Link to="/services">
                <Button className="bg-[#1e3a5f] hover:bg-[#2c5282] text-white px-6 py-3 rounded-full transition-all duration-300 hover:scale-105">
                  Learn More About Our Services
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=800&q=80"
                alt="Medical equipment"
                className="rounded-lg shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-[#1e3a5f]">
            Why Choose Kalorama Medical?
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="border-2 border-blue-100 hover:border-[#7bb5d4] transition-all duration-300 hover:shadow-lg">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-[#7bb5d4] rounded-full flex items-center justify-center mx-auto mb-6">
                  <Check className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-2xl font-semibold mb-4 text-[#1e3a5f]">Affordability</h3>
                <p className="text-gray-600">
                  We provide a wide variety of affordable medical equipment and supplies. We work with insurance providers to make quality equipment accessible.
                </p>
              </CardContent>
            </Card>
            <Card className="border-2 border-blue-100 hover:border-[#7bb5d4] transition-all duration-300 hover:shadow-lg">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-[#7bb5d4] rounded-full flex items-center justify-center mx-auto mb-6">
                  <Check className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-2xl font-semibold mb-4 text-[#1e3a5f]">Quality</h3>
                <p className="text-gray-600">
                  Our goal is to provide products that meet a 100% quality standard. We partner with trusted manufacturers to ensure reliability.
                </p>
              </CardContent>
            </Card>
            <Card className="border-2 border-blue-100 hover:border-[#7bb5d4] transition-all duration-300 hover:shadow-lg">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-[#7bb5d4] rounded-full flex items-center justify-center mx-auto mb-6">
                  <Check className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-2xl font-semibold mb-4 text-[#1e3a5f]">Durability</h3>
                <p className="text-gray-600">
                  Our products have a proven track record of longevity. Invest in equipment that will serve you well for years to come.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-br from-[#1e3a5f] to-[#2c5282] text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-xl mb-8 text-blue-100 max-w-2xl mx-auto">
            Contact our team today for personalized recommendations and expert guidance
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button size="lg" className="bg-white text-[#1e3a5f] hover:bg-blue-50 px-8 py-6 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105">
                Contact Us
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/products">
              <Button size="lg" variant="outline" className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-[#1e3a5f] px-8 py-6 rounded-full shadow-lg hover:shadow-xl transition-all duration-300">
                View All Products
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
